import { useEffect } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useActiveStoreType } from "@/hooks/use-active-store-type";
import { allNavItems, type StoreType } from "./nav-config";

/** Početna stranica za svaki tip trgovine. */
export const HOME_BY_STORE_TYPE: Record<StoreType, string> = {
  woocommerce: "/orders",
  wholesale: "/wholesale/orders",
};

const matchesItem = (pathname: string, to: string) =>
  pathname === to || pathname.startsWith(to.endsWith("/") ? to : `${to}/`);

/**
 * Kamo preusmjeriti kad tip aktivne trgovine ne odgovara trenutnoj stranici:
 * admin prebaci na Veleprodaju dok gleda maloprodajne narudžbe (ili obratno).
 * Vraća `null` kad je stranica u redu ili tip još nije poznat. Navigacija je
 * izvor istine o tome koje stranice pripadaju kojem tipu; provjera se radi kao
 * admin jer ovdje odlučujemo samo o tipu trgovine, ne o pravima.
 */
export function storeTypeRedirectTarget(pathname: string, storeType: StoreType | undefined): string | null {
  if (!storeType) return null;
  const allowed = allNavItems({ isAdmin: true, storeType });
  if (allowed.some((item) => matchesItem(pathname, item.to))) return null;
  const isWholesalePath = matchesItem(pathname, "/wholesale");
  if (storeType === "wholesale" && isWholesalePath) return null;
  if (storeType === "woocommerce" && !isWholesalePath) return null;
  return HOME_BY_STORE_TYPE[storeType];
}

export function useStoreTypeRedirect() {
  const storeType = useActiveStoreType();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const target = storeTypeRedirectTarget(pathname, storeType);
    if (target) navigate({ to: target, replace: true });
  }, [pathname, storeType, navigate]);
}
